// TEXT_BUBBLE 메타 폼 헬퍼 — EppiCostumeFormPage 에서 분리.
// V200 painter 기준. slice_* 4필드는 입력/표시 대상이 아님(legacy NULL 시드).

import {TextBubbleMeta} from '../../types/eppiCostume';

export type BubbleMetaState = Record<keyof TextBubbleMeta, number | ''>;

/**
 * V200 (painter) 필수 텍스트 메타 키.
 * V193 의 slice_* 4필드는 painter 모드에서 불필요 (NULL 시드) → 필수 검증 대상 제외.
 * slice_* 컬럼은 legacy 보존을 위해 polymeric type 에 남아 있을 뿐 입력은 받지 않음.
 */
export const REQUIRED_BUBBLE_META_KEYS: (keyof TextBubbleMeta)[] = [
    'padL',
    'padT',
    'padR',
    'padB',
    'textSizeSp',
    'maxWidthDp',
    'maxChars',
];

/** V196/V197: TEXT_BUBBLE 의 선택적 메타 키 — 박스 압축 방지용. */
export const OPTIONAL_BUBBLE_META_KEYS: (keyof TextBubbleMeta)[] = [
    'minWidthDp',
    'minHeightDp',
];

/** 폼 state 매핑 대상 = 필수 7개 + 선택 2개. slice_* 는 V200 painter 에서 입력/표시 안 함. */
export const ALL_BUBBLE_META_KEYS: (keyof TextBubbleMeta)[] = [
    ...REQUIRED_BUBBLE_META_KEYS,
    ...OPTIONAL_BUBBLE_META_KEYS,
];

/** 모든 필드를 빈값('')으로 초기화한 폼 state. */
export function emptyBubbleMeta(): BubbleMetaState {
    return ALL_BUBBLE_META_KEYS.reduce((acc, key) => {
        acc[key] = '';
        return acc;
    }, {} as BubbleMetaState);
}

/** 서버 detail 값으로 폼 state prefill (null → ''). */
export function bubbleMetaFromDetail(detail: TextBubbleMeta): BubbleMetaState {
    return ALL_BUBBLE_META_KEYS.reduce((acc, key) => {
        const v = detail[key];
        acc[key] = v == null ? '' : v;
        return acc;
    }, {} as BubbleMetaState);
}

/**
 * 7개 필수 필드(pad×4 + textSizeSp + maxWidthDp + maxChars)가 모두 채워졌는지.
 * minWidthDp/minHeightDp 같은 선택 메타는 판정에서 제외.
 * V200 painter 에서는 slice_* 도 입력 대상이 아니므로 제외.
 */
export function isBubbleMetaComplete(meta: BubbleMetaState): boolean {
    return REQUIRED_BUBBLE_META_KEYS.every((key) => meta[key] !== '');
}

/** TEXT_BUBBLE 숫자 입력 필드 정의 — 그룹/라벨/단위/도움말. */
export interface BubbleFieldDef {
    key: keyof TextBubbleMeta;
    label: string;
}

export const BUBBLE_PAD_FIELDS: BubbleFieldDef[] = [
    {key: 'padL', label: 'pad L'},
    {key: 'padT', label: 'pad T'},
    {key: 'padR', label: 'pad R'},
    {key: 'padB', label: 'pad B'},
];

export const BUBBLE_TEXT_FIELDS: BubbleFieldDef[] = [
    {key: 'textSizeSp', label: '글자 크기 (sp)'},
    {key: 'maxWidthDp', label: '최대 너비 (dp)'},
    {key: 'maxChars', label: '최대 글자 수'},
];

/**
 * V196/V197 선택 메타 — 캐릭터 박힌 말풍선 디자인 전용.
 * 단순 사각/꼬리 디자인은 비워두면 자유 가변(0/null=강제 없음).
 */
export const BUBBLE_OPTIONAL_FIELDS: {
    key: keyof TextBubbleMeta;
    label: string;
    helper: string;
}[] = [
    {
        key: 'minWidthDp',
        label: 'minWidthDp (dp)',
        helper: '짧은 글자에도 박스 최소 폭 강제. 캐릭터 박힌 디자인용. 0/빈 값 = 강제 없음.',
    },
    {
        key: 'minHeightDp',
        label: 'minHeightDp (dp)',
        helper: '짧은 글자에도 박스 최소 높이 강제. 이미지 가로 stretched 방지용. 0/빈 값 = 강제 없음.',
    },
];
